import {
  getActivePresentationRefViaIntents,
  type FactSetPresentationRef,
} from "./factset/intentBridgeService";
import { getDocumentId } from "./storageService";

// `getDocumentId()` prefixes the FactSet `openPresentationId` with this when
// the storage layer's active-presentation override is set. Splitting on the
// prefix lets us derive a minimal `FactSetPresentationRef` synchronously,
// without an extra intent round-trip, so the identity returned by
// `capturePresentationKey` matches the file the user submitted from even
// when the FactSet bridge is slow to respond.
const PRESENTATION_ID_PREFIX = "pres_";

/**
 * Atomic snapshot of "which presentation does this operation belong to?".
 *
 * - `documentId` is the storage layer's hash of `Office.context.document.url`
 *   (or a session-scoped fallback for unsaved files). It is the key under
 *   which `useTabs` persists chat history, so it MUST always be filled.
 * - `presentationRef` is the FactSet ref (`{ openPresentationId }`) used to
 *   target a specific open presentation when raising intents. May be `null`
 *   when the FactSet bridge is unavailable (non-FactSet host, Office.js
 *   fallback path) — callers SHOULD pass it to intent helpers as-is; the
 *   helpers no-op when the ref is missing.
 *
 * Captured once at submit time and threaded through every coordinator flow
 * so a query started in file A always applies to file A even if the user
 * switches active presentation mid-flight.
 */
export interface PresentationKey {
  documentId: string;
  presentationRef: FactSetPresentationRef | null;
}

type Logger = (message: string) => void;

/**
 * Snapshot the active presentation identity.
 *
 * Identity is anchored to the SYNCHRONOUS `getDocumentId()` read — this is
 * "the file the user committed to right now". `useTabs` keeps that value
 * aligned with the active PowerPoint window via its 2s FactSet poll, so the
 * sync read is the source of truth and avoids a critical race the older
 * "round-trip first" version had:
 *
 *   1. user clicks Send on file A,
 *   2. `getActivePresentationRefViaIntents()` round-trip is in flight,
 *   3. user switches PowerPoint focus to file B during the round-trip,
 *   4. the round-trip resolves with B's ref,
 *   5. (old behavior) `setActivePresentationId(B)` flips the storage
 *      override and `getDocumentId()` now returns `pres_B`,
 *   6. the function returns identity for B, the entire chat flow gets
 *      retargeted to B, and the source-tab `isLoading=true` in A's storage
 *      envelope is orphaned with no matching in-flight ticket — so on
 *      switch-back to A `reconcileLoadingWithRegistry` demotes it to
 *      "Interrupted", visibly losing the loading state.
 *
 * Post-fix: the sync `documentId` is locked in BEFORE the round-trip, and
 * the live ref is only ADOPTED (for its display `name` / `fullName`) when
 * its `openPresentationId` still matches the sync-captured one. We do NOT
 * write the storage-layer override here anymore — that's owned by `useTabs`
 * and `capturePresentationKey` having its own side effect was the very
 * thing that let the race corrupt routing in the first place.
 */
export async function capturePresentationKey(log?: Logger): Promise<PresentationKey> {
  // (1) Anchor identity synchronously. This is the file the user is on
  //     RIGHT NOW per the storage layer's active-presentation override.
  const documentId = getDocumentId();
  // Derive a minimal ref from the override-encoded documentId so flows
  // can route writes to the correct presentation even if the FactSet
  // round-trip below fails or returns a now-stale "live" ref.
  let presentationRef: FactSetPresentationRef | null = documentId.startsWith(
    PRESENTATION_ID_PREFIX
  )
    ? { openPresentationId: documentId.slice(PRESENTATION_ID_PREFIX.length) }
    : null;

  // (2) Best-effort enrichment for `name` / `fullName` (used by the
  //     BackgroundFlowBanner label and per-flow `presentationDisplayName`).
  //     Only adopt the live ref when it agrees with the sync-captured id;
  //     if the user has switched windows during the round-trip, the live
  //     ref points at a different file and merging it in would silently
  //     re-target the flow at the wrong presentation.
  try {
    const liveRef = await getActivePresentationRefViaIntents(log);
    if (
      liveRef?.openPresentationId &&
      presentationRef?.openPresentationId &&
      liveRef.openPresentationId === presentationRef.openPresentationId
    ) {
      presentationRef = liveRef;
    }
  } catch (error) {
    // Bridge unavailable or host unresponsive — keep the minimal ref we
    // derived from the documentId. Intent helpers tolerate a null ref by
    // targeting the active presentation, which matches today's
    // pre-multi-file behavior.
    log?.(
      `[presentationIdentity] Failed to enrich FactSet presentation ref: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }

  return { documentId, presentationRef };
}

/**
 * Equality predicate for routing decisions ("is this flow's home presentation
 * the one the user is looking at right now?").
 *
 * - If BOTH keys have a `presentationRef.openPresentationId`, the FactSet id
 *   is authoritative (survives renames / Save As within a session).
 * - Otherwise we fall back to `documentId` equality (also survives renames
 *   because it's a hash of `Office.context.document.url`, which the host
 *   updates on Save As).
 * - A null key is never equal to anything (including another null).
 */
export function presentationKeyEquals(
  a: PresentationKey | null | undefined,
  b: PresentationKey | null | undefined
): boolean {
  if (!a || !b) {
    return false;
  }

  const refA = a.presentationRef?.openPresentationId;
  const refB = b.presentationRef?.openPresentationId;
  if (refA && refB) {
    return refA === refB;
  }

  return a.documentId === b.documentId;
}

/**
 * Stable string key for in-memory maps (per-tab badges, in-flight registry).
 * Combines BOTH identity sources so a session-id-less host (no FactSet) still
 * gets a unique key per document, and an unsaved file (no document URL) still
 * gets a unique key per open presentation.
 */
export function presentationKeyHash(key: PresentationKey): string {
  const refPart = key.presentationRef?.openPresentationId ?? "no-ref";
  return `${key.documentId}::${refPart}`;
}
